"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const adobe_triangle_1 = require("adobe_triangle");
const array_flatten_1 = __importDefault(require("array-flatten"));
const geom_1 = require("../common/geom");
const common_1 = require("../common");
const triangulatePolygon_1 = require("./triangulatePolygon");
class TriangulationError extends Error {
    constructor(message, faceIndex) {
        super(message);
        this.faceIndex = faceIndex;
        this.name = 'TriangulationError';
    }
}
exports.TriangulationError = TriangulationError;
function verticesFromTriangleOutput(crease, triangulation) {
    const vertices = [];
    const verticesForwardMapping = [...Array(crease.vertices.length)];
    for (let i = 0; i < triangulation.numberofpoints; i++) {
        const parent = triangulation.pointmarkerlist[i] >= 0
            ? triangulation.pointmarkerlist[i] : undefined;
        if (parent !== undefined) {
            verticesForwardMapping[parent] = [i];
        }
        const triangleVertex = {
            position2D: [triangulation.pointlist[2 * i], triangulation.pointlist[2 * i + 1]],
            parent,
            index: i,
        };
        vertices.push(triangleVertex);
    }
    return { vertices, verticesForwardMapping };
}
function facesFromTriangleOutput(crease, triangulation) {
    const faces = [];
    const facesForwardMapping = [...Array(crease.faces.length)].map(() => []);
    for (let i = 0; i < triangulation.numberoftriangles; i++) {
        const vertices = [
            triangulation.trianglelist[3 * i],
            triangulation.trianglelist[3 * i + 1],
            triangulation.trianglelist[3 * i + 2],
        ];
        const parent = triangulation.trianglemarkerlist[i];
        if (parent < 0) {
            throw new Error(`No parent for triangle: ${vertices}`);
        }
        facesForwardMapping[parent].push(i);
        const triangleFace = { parent, vertices };
        faces.push(triangleFace);
    }
    return { faces, facesForwardMapping };
}
function edgesFromFaces(crease, triangulation, vertices, faces) {
    // Use edge keys to look up edge parents and edge indices.
    const getEdgeKey = (v1, v2) => `${Math.min(v1, v2)},${Math.max(v1, v2)}`;
    const edgeParentLookup = {};
    const edgeIndexLookup = {};
    // Store parents in lookup.
    for (let i = 0; i < triangulation.numberofedges; i++) {
        const parent = triangulation.edgemarkerlist[i];
        if (parent >= 0) {
            const v1 = triangulation.edgelist[2 * i];
            const v2 = triangulation.edgelist[2 * i + 1];
            edgeParentLookup[getEdgeKey(v1, v2)] = parent;
        }
    }
    // Create edges from faces.
    const edges = [];
    const edgesForwardMapping = crease.edges.map(() => []);
    faces.forEach((face, faceIndex) => {
        for (const i of [0, 1, 2]) {
            const v1 = face.vertices[i];
            const v2 = face.vertices[(i + 1) % 3];
            const v3 = face.vertices[(i + 2) % 3];
            const edgeKey = getEdgeKey(v1, v2);
            if (edgeKey in edgeIndexLookup) {
                // We've already created an edge from v2 to v1, and now we have a triangle with an
                // edge from v1 to v2. See if the parents of these triangle edges are boundary
                // edges on opposite sides of a slit.
                const edge = edges[edgeIndexLookup[edgeKey]];
                if (edge.parent !== undefined && crease.edges[edge.parent].isBoundary) {
                    // The triangle edge from v2 to v1 has a boundary edge as parent. Find the
                    // boundary edge that's parent of the triangle edge from v1 to v2.
                    const creaseEdge = crease.edges[edge.parent];
                    const reverseCreaseEdge = crease.edges.find(e => e.isBoundary
                        && e.vertex1 === creaseEdge.vertex2 && e.vertex2 === creaseEdge.vertex1);
                    if (reverseCreaseEdge === undefined) {
                        throw new Error('Cannot find reverse boundary edge.');
                    }
                    // TODO: It's not clear which triangle edge goes with which parent edge.
                    const parent = reverseCreaseEdge.index;
                    edgesForwardMapping[parent].push(edges.length);
                    edges.push({
                        parent,
                        edgeVertex1: v1,
                        edgeVertex2: v2,
                        leftVertex: v3,
                        leftFace: faceIndex,
                        targetLength: edge.targetLength,
                    });
                }
                else {
                    // The current triangle is on the right side of the triangle edge from v2 to v1.
                    edge.rightVertex = v3;
                    edge.rightFace = faceIndex;
                }
            }
            else {
                // We didn't find a previously created edge, so create one now.
                const parent = edgeKey in edgeParentLookup ? edgeParentLookup[edgeKey] : undefined;
                if (parent !== undefined) {
                    edgesForwardMapping[parent].push(edges.length);
                }
                edgeIndexLookup[edgeKey] = edges.length;
                const targetLength = common_1.vec.distance(vertices[v1].position2D, vertices[v2].position2D);
                edges.push({
                    parent,
                    edgeVertex1: v1,
                    edgeVertex2: v2,
                    leftVertex: v3,
                    leftFace: faceIndex,
                    targetLength,
                });
            }
        }
    });
    return { edges, edgesForwardMapping };
}
function flipEdge(edge) {
    if (edge.rightFace !== undefined && edge.rightVertex !== undefined) {
        let temp = edge.edgeVertex1;
        edge.edgeVertex1 = edge.edgeVertex2;
        edge.edgeVertex2 = temp;
        temp = edge.leftFace;
        edge.leftFace = edge.rightFace;
        edge.rightFace = temp;
        temp = edge.leftVertex;
        edge.leftVertex = edge.rightVertex;
        edge.rightVertex = temp;
        return true;
    }
    return false;
}
function orderEdges(crease, vertices, verticesForwardMapping, edges, edgesForwardMapping) {
    crease.edges.forEach(creaseEdge => {
        const edgeIndices = edgesForwardMapping[creaseEdge.index].slice();
        let prevVertexIndex = verticesForwardMapping[creaseEdge.vertex1.index][0];
        const edgeMatches = (edgeIndex) => edges[edgeIndex].edgeVertex1 === prevVertexIndex
            || edges[edgeIndex].edgeVertex2 === prevVertexIndex;
        const orderedEdgeIndices = [];
        while (edgeIndices.length) {
            const arrayIndex = edgeIndices.findIndex(edgeMatches);
            if (arrayIndex < 0) {
                throw new Error('Cannot complete chain of triangle edges.');
            }
            let nextEdgeIndex = edgeIndices[arrayIndex];
            let nextEdge = edges[nextEdgeIndex];
            if (nextEdge.edgeVertex2 === prevVertexIndex) {
                // Edge goes the wrong way. See if we can flip it.
                if (!flipEdge(nextEdge)) {
                    // We can't flip a boundary edge. See if we can exchange it for a boundary
                    // edge going the other way.
                    /* eslint-disable-next-line no-loop-func */
                    const reverseEdgeIndex = edges.findIndex(e => e.rightFace === undefined
                        && e.edgeVertex1 === prevVertexIndex
                        && e.edgeVertex2 === nextEdge.edgeVertex1);
                    if (reverseEdgeIndex >= 0) {
                        nextEdgeIndex = reverseEdgeIndex;
                        nextEdge = edges[nextEdgeIndex];
                        nextEdge.parent = creaseEdge.index;
                    }
                    else {
                        throw new Error('Could not find matching boundary edge.');
                    }
                }
            }
            edgeIndices.splice(arrayIndex, 1);
            prevVertexIndex = nextEdge.edgeVertex2;
            orderedEdgeIndices.push(nextEdgeIndex);
        }
        edgesForwardMapping[creaseEdge.index] = orderedEdgeIndices;
    });
}
function getNormalizer(crease) {
    const bounds = crease.getBounds();
    const center = common_1.vec.divide(common_1.vec.add(bounds.min, bounds.max), 2);
    const size = common_1.vec.subtract(bounds.max, bounds.min);
    const scale = Math.max(size[0], size[1]);
    return p => common_1.vec.divide(common_1.vec.subtract(p, center), scale);
}
exports.getNormalizer = getNormalizer;
function refineTriangulation(pointlist, pointmarkerlist, segmentlist, segmentmarkerlist, trianglelist, trianglemarkerlist, minimumAngle) {
    const options = {
        pointlist,
        pointmarkerlist,
        segmentlist,
        segmentmarkerlist,
        trianglelist,
        trianglemarkerlist,
        switches: `q${minimumAngle}`,
    };
    return adobe_triangle_1.triangulate(options);
}
function createUnrefinedTriangulation(pointlist, pointmarkerlist, segmentlist, segmentmarkerlist, trianglelist, trianglemarkerlist) {
    const triangulation = {
        numberofpoints: pointmarkerlist.length,
        pointlist: new Float64Array(pointlist),
        pointmarkerlist: new Int32Array(pointmarkerlist),
        numberofsegments: segmentmarkerlist.length,
        segmentlist: new Int32Array(segmentlist),
        segmentmarkerlist: new Int32Array(segmentmarkerlist),
        numberofedges: segmentmarkerlist.length,
        edgelist: new Int32Array(segmentlist),
        edgemarkerlist: new Int32Array(segmentmarkerlist),
        numberoftriangles: trianglemarkerlist.length,
        trianglelist: new Int32Array(trianglelist),
        trianglemarkerlist: new Int32Array(trianglemarkerlist),
        neighborlist: new Int32Array(),
    };
    return triangulation;
}
/**
 * Returns a triangle mesh obtained by triangulating each face of the given Crease object and
 * possibly refining that triangulation by adding Steiner points to achieve the specified minimum
 * angle.
 * @param crease A Crease object.
 * @param minimumAngle The minimum angle (in degrees) permitted after refining the quality of the
 * triangulation. Specifying 0 results in no quality refinement at all, yielding a constrained
 * Delaunay triangulation of the Crease faces. The default value of 20 degrees provides a high
 * quality triangulation, while a lower value like 10 degrees may also be acceptable. The triangle
 * algorithm is theoretically guaranteed to terminate for values of 20.7 degrees or smaller; it may
 * succeed for minimum angles as high as 33 degrees; it often does not terminate for minimum angles
 * above 34 degrees.
 * @see https://www.cs.cmu.edu/~quake/triangle.q.html
 */
function creaseToTriangleMesh(crease, minimumAngle = 20) {
    // Create a function that normalizes positions to the unit square [-0.5, 0.5]^2.
    const normalize = getNormalizer(crease);
    // First calc all points and segments.  May need to discretize curved edges.
    const pointlist = array_flatten_1.default(crease.vertices.map(vertex => normalize(vertex.position)));
    const segmentlist = [];
    // const segmentattributes = [];
    // Keep track of original index in crease object.
    const pointmarkerlist = crease.vertices.map(vertex => vertex.index);
    const segmentmarkerlist = [];
    // Keep track of possible curved segment discretization.
    const allSegmentInteriorPoints = [];
    crease.edges.forEach((edge) => {
        segmentlist.push(edge.vertex1.index);
        const segmentInteriorPoints = [];
        if (edge.controlPoints.length) {
            // TODO: Subdivide curve adaptively according to curvature.
            const subdivCount = 10;
            for (let i = 1; i < subdivCount; i++) {
                const t = i / subdivCount;
                const p = normalize(geom_1.geom.evaluateBezier(edge.allPoints, t));
                const pointIndex = Math.floor(pointlist.length / 2);
                segmentlist.push(pointIndex);
                segmentlist.push(pointIndex);
                segmentmarkerlist.push(edge.index);
                pointlist.push(p[0]);
                pointlist.push(p[1]);
                pointmarkerlist.push(-1);
                segmentInteriorPoints.push(pointIndex);
            }
        }
        allSegmentInteriorPoints.push(segmentInteriorPoints);
        segmentlist.push(edge.vertex2.index);
        segmentmarkerlist.push(edge.index);
    });
    // Triangulate each face without adding steiner points.
    const trianglelist = [];
    const trianglemarkerlist = [];
    crease.faces.forEach((face) => {
        // Only pass in points on this face, otherwise outside points may affect triangulation.
        const facePoints = [];
        const facePointIndices = [];
        face.edgeLoops.forEach(edgeLoop => {
            const loopPoints = [];
            edgeLoop.edgeRefs.forEach((edgeRef) => {
                facePointIndices.push(edgeRef.vertex1.index);
                loopPoints.push([
                    pointlist[2 * edgeRef.vertex1.index],
                    pointlist[2 * edgeRef.vertex1.index + 1],
                ]);
                // TODO: expose index from edgeref
                let segmentInteriorPoints = allSegmentInteriorPoints[edgeRef.edge.index];
                if (edgeRef.isReversed) {
                    segmentInteriorPoints = segmentInteriorPoints.slice().reverse();
                }
                segmentInteriorPoints.forEach((vertexIndex) => {
                    facePointIndices.push(vertexIndex);
                    loopPoints.push([pointlist[2 * vertexIndex], pointlist[2 * vertexIndex + 1]]);
                });
            });
            facePoints.push(loopPoints);
        });
        // Triangulate this face.
        let faceTriangulation;
        try {
            faceTriangulation = triangulatePolygon_1.triangulatePolygon(facePoints);
        }
        catch (e) {
            // If an exception was thrown, wrap it in a TriangulationError so we can expose the
            // face index that failed.
            throw new TriangulationError(e.message, face.index);
        }
        // Add triangles and face indices.
        faceTriangulation.forEach(triangle => {
            trianglelist.push(facePointIndices[triangle[0]]);
            trianglelist.push(facePointIndices[triangle[1]]);
            trianglelist.push(facePointIndices[triangle[2]]);
            trianglemarkerlist.push(face.index);
        });
    });
    let triangulation;
    if (minimumAngle > 0) {
        triangulation = refineTriangulation(pointlist, pointmarkerlist, segmentlist, segmentmarkerlist, trianglelist, trianglemarkerlist, minimumAngle);
    }
    else {
        triangulation = createUnrefinedTriangulation(pointlist, pointmarkerlist, segmentlist, segmentmarkerlist, trianglelist, trianglemarkerlist);
    }
    const { vertices, verticesForwardMapping } = verticesFromTriangleOutput(crease, triangulation);
    const { faces, facesForwardMapping } = facesFromTriangleOutput(crease, triangulation);
    const { edges, edgesForwardMapping } = edgesFromFaces(crease, triangulation, vertices, faces);
    orderEdges(crease, vertices, verticesForwardMapping, edges, edgesForwardMapping);
    const triangleMesh = {
        vertices,
        edges,
        faces,
        verticesForwardMapping,
        edgesForwardMapping,
        facesForwardMapping,
    };
    return triangleMesh;
}
exports.creaseToTriangleMesh = creaseToTriangleMesh;
//# sourceMappingURL=creaseToTriangleMesh.js.map